import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import CompanyContext from '../../../auth/CompanyContext';
import Loading from '../../Loading/Loading';

const PortalHoldings = (props) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(props.companySettings?.CompanyPortalDatabase || '').trim() !== '';

    const { callback, onWidgetLoad, onAddCallback } = props;

    const getValues = useCallback((width) => {
        let col1 = 0, col2 = 0, graphHeight = 0;
        switch (width) {
            case 4:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 6:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 12:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            default:
        }
        return { col1, col2, graphHeight };
    }, []);

    const initialValues = getValues(props.width);
    const [holdings, setHoldings] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [col1, setCol1] = useState(initialValues.col1);
    const [col2, setCol2] = useState(initialValues.col2);
    const [graphHeight, setGraphHeight] = useState(initialValues.graphHeight);
    const [styleSettings, setStyleSettings] = useState({});
    const [initialDate, setInitialDate] = useState('');
    const [date, setDate] = useState(null);

    const refreshRef = useRef(() => { });
    const styleSettingsRef = useRef({});

    const toNumber = useCallback((val) => {
        const parsed = Number(val);
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);

    const getStyleSettings = useCallback(async () => {
        if (!hasCompanyPortalDatabase) return;

        const styleNames = ['tranSummaryPeriod'];
        const params = new URLSearchParams();
        styleNames.forEach(name => params.append('styleNames[]', name));

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/getStyleSettings?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();
            const nextStyleSettings = response.styleSettings || {};
            styleSettingsRef.current = nextStyleSettings;
            setStyleSettings(nextStyleSettings);
        } catch (err) {
            console.error(err);
            styleSettingsRef.current = {};
            setStyleSettings({});
        }
    }, [props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getHoldDates = useCallback(async () => {
        if (!hasCompanyPortalDatabase) return '';

        const payload = {
            port: '',
            household: props.selectedHousehold
        };

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/posDates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                },
                body: JSON.stringify(payload)
            });
            const response = await results.json();
            if (response.code === 200 && response.data && response.data.length > 0) {
                return response.data[0].fDate;
            }
            return '';
        } catch (err) {
            console.log(err);
            return '';
        }
    }, [props.selectedHousehold, props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getHoldings = useCallback(async (initialDateIn) => {
        if (!hasCompanyPortalDatabase) {
            setHoldings([]);
            setTotal(0);
            setLoading(false);
            return;
        }

        setLoading(true);

        const household = props.selectedHousehold || '';
        const dateObj = initialDateIn ? new Date(initialDateIn) : null;
        const params = new URLSearchParams({
            household,
            date: dateObj ? dateObj.toISOString() : ''
        });

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/holdingsSnapshot?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();
            if (response.code === 200) {
                const rows = response.data || [];
                const totalValue = rows.reduce((sum, row) => sum + toNumber(row && row.marketValue), 0);
                setHoldings(rows);
                setTotal(totalValue);
                setLoading(false);
                setDate(dateObj);
            } else {
                setHoldings([]);
                setTotal(0);
                setLoading(false);
                setDate(dateObj);
            }
        } catch (err) {
            console.log(err);
            setHoldings([]);
            setTotal(0);
            setLoading(false);
            setDate(dateObj);
        }
    }, [props.selectedHousehold, props.idToken, companyCtx.company, toNumber, hasCompanyPortalDatabase]);

    const onSubmit = useCallback(async () => {
        const nextInitialDate = await getHoldDates();
        setInitialDate(nextInitialDate);
        getHoldings(nextInitialDate);
    }, [getHoldDates, getHoldings]);

    const runRefresh = useCallback(() => {
        setLoading(true);
        onSubmit();
    }, [onSubmit]);

    useEffect(() => {
        refreshRef.current = runRefresh;
    }, [runRefresh]);

    useEffect(() => {
        let active = true;

        const init = async () => {
            await getStyleSettings();
            if (!active) return;

            callback((width) => {
                if (!active) return;
                const values = getValues(width);
                setGraphHeight(values.graphHeight);
                setCol1(values.col1);
                setCol2(values.col2);
            });

            if (onWidgetLoad) {
                onWidgetLoad();
                onAddCallback(() => {
                    refreshRef.current();
                });
            }
        };

        init();

        return () => {
            active = false;
        };
    }, [hasCompanyPortalDatabase]);

    const numberWithCommas = (value) => {
        const num = toNumber(value);
        return '$' + num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const snapshotDate = date instanceof Date && !Number.isNaN(date.getTime())
        ? date.toLocaleDateString('en-US')
        : '';

    void styleSettings;
    void initialDate;
    void styleSettingsRef;
    void col1;
    void col2;

    return (
        <Card className='w-100 m-0'>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h4>Holdings</h4>
                {props.icon}
            </CardHeader>
            <CardBody className='p-3'>
                {loading && <Loading />}
                {!loading && (
                    <>
                        {snapshotDate && (
                            <div className='holdingsSnapshotHeader'>
                                <label>{snapshotDate}</label>
                            </div>
                        )}
                        {holdings.length > 0 ? (
                            <div style={{ minHeight: graphHeight + 'px' }}>
                                <table className='holdingsSnapshot'>
                                    <tbody>
                                        {holdings.slice(0, 5).map((row, idx) => (
                                            <tr key={`${row.assetAllocation || 'holding'}-${idx}`}>
                                                <td className='holdingsLabel'>
                                                    <label style={{ fontWeight: 'bold' }}>{row.assetAllocation}</label>
                                                </td>
                                                <td className='holdingsValue'>
                                                    <label style={{ fontWeight: 'bold' }}>{numberWithCommas(row.marketValue)}</label>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <br />
                                <table className='holdingsSnapshot'>
                                    <tbody>
                                        <tr>
                                            <td className='totalHoldingsLabel'>
                                                <label style={{ fontWeight: 'bold' }}>Total</label>
                                            </td>
                                            <td className='totalHoldingsValue'>
                                                <label style={{ fontWeight: 'bold' }}>{numberWithCommas(total)}</label>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className='widgetNoDataWithDate'>No holdings during the period</div>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default PortalHoldings;
